import React, { useState, useEffect } from 'react';
import { LineHeightOutlined, UploadOutlined } from '@ant-design/icons';
import PageHeading from '../../components/PageHeading';
import { Form, Input, message, Upload, Button, Checkbox, Row, Col } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../utils/axios';

const AddNews = () => {
  const { id } = useParams();
  const isUpdate = Boolean(id);
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [thumbnail, setThumbnail] = useState(null);
  const [banner, setBanner] = useState(null);
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isUpdate) {
      fetchNewsDetail();
    } else {
      form.resetFields();
      setThumbnail(null);
      setVideo(null);
      setBanner(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchNewsDetail = async () => {
    try {
      const { data } = await axiosInstance.get(`news_and_events/news/${id}/`);
      form.setFieldsValue({
        heading: data.heading,
        content: data.content,
        publish: data.publish,
      });

      if (data.thumbnail) {
        setThumbnail({
          uid: '-1',
          name: 'thumbnail.jpg',
          status: 'done',
          url: data.thumbnail,
        });
      }

      if (data.banner) {
        setBanner({
          uid: '-2',
          name: 'banner.jpg',
          status: 'done',
          url: data.banner,
        });
      }

      if (data.video) {
        setVideo({
          uid: '-3',
          name: 'video.mp4',
          status: 'done',
          url: data.video,
        });
      }
    } catch {
      message.error('Failed to load news details');
    }
  };

  const validateImage = (file) => {
    const isImage = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isImage) {
      message.error('Only JPG/PNG files are allowed!');
      return Upload.LIST_IGNORE;
    }
    if (!isLt2M) {
      message.error('Image must be smaller than 2MB!');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const validateVideo = (file) => {
    const isMp4 = file.type === 'video/mp4';
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isMp4) {
      message.error('Only MP4 video is allowed!');
      return Upload.LIST_IGNORE;
    }
    if (!isLt10M) {
      message.error('Video must be smaller than 10MB!');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const getOriginFile = (file) => {
    return file?.originFileObj || null;
  };

  const handleFinish = async (values) => {
    if (!thumbnail && !isUpdate) {
      message.error('Thumbnail is required');
      return;
    }

    setLoading(true);
    const fd = new FormData();
    fd.append('heading', values.heading);
    fd.append('publish', values.publish || false);
    fd.append('content', values.content);

    const thumbFile = getOriginFile(thumbnail);
    const bannerFile = getOriginFile(banner);
    const videoFile = getOriginFile(video);

    if (thumbFile) fd.append('thumbnail', thumbFile);
    if (bannerFile) fd.append('banner', bannerFile);
    if (videoFile) fd.append('video', videoFile);

    try {
      if (isUpdate) {
        await axiosInstance.patch(`news_and_events/news/${id}/`, fd);
        message.success('News updated successfully');
      } else {
        await axiosInstance.post('news_and_events/news/', fd);
        message.success('News added successfully');
      }
      navigate('/admin/news');
    } catch (err) {
      console.error(err);
      message.error('Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <PageHeading name={isUpdate ? 'Update News' : 'Add News'} icon={<LineHeightOutlined />} />
      <Form layout="vertical" form={form} name="news_form" onFinish={handleFinish}>
        <Form.Item
          label="Heading"
          name="heading"
          rules={[{ required: true, message: 'Please enter news heading' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="News Content"
          name="content"
          rules={[{ required: true, message: 'Please enter news content' }]}
        >
          <Input.TextArea rows={6} />
        </Form.Item>

        <Form.Item label="Media Uploads">
          <Row gutter={16}>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                label="Thumbnail (Required)"
                required
                validateStatus={!thumbnail && !isUpdate ? 'error' : 'success'}
                help={!thumbnail && !isUpdate && 'Thumbnail is required'}
              >
                <Upload
                  maxCount={1}
                  fileList={thumbnail ? [thumbnail] : []}
                  beforeUpload={(file) => {
                    const valid = validateImage(file);
                    if (valid !== true) return valid;
                    setThumbnail({
                      uid: file.uid,
                      name: file.name,
                      status: 'done',
                      originFileObj: file,
                    });
                    return false;
                  }}
                  onRemove={() => setThumbnail(null)}
                >
                  <Button icon={<UploadOutlined />}>Upload Thumbnail</Button>
                </Upload>
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <Form.Item label="Banner (Optional)">
                <Upload
                  maxCount={1}
                  fileList={banner ? [banner] : []}
                  beforeUpload={(file) => {
                    const valid = validateImage(file);
                    if (valid !== true) return valid;
                    setBanner({
                      uid: file.uid,
                      name: file.name,
                      status: 'done',
                      originFileObj: file,
                    });
                    return false;
                  }}
                  onRemove={() => setBanner(null)}
                >
                  <Button icon={<UploadOutlined />}>Upload Banner</Button>
                </Upload>
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <Form.Item label="Video (Optional)">
                <Upload
                  maxCount={1}
                  fileList={video ? [video] : []}
                  beforeUpload={(file) => {
                    const valid = validateVideo(file);
                    if (valid !== true) return valid;
                    setVideo({
                      uid: file.uid,
                      name: file.name,
                      status: 'done',
                      originFileObj: file,
                    });
                    return false;
                  }}
                  onRemove={() => setVideo(null)}
                >
                  <Button icon={<UploadOutlined />}>Upload Video</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form.Item>

        <Form.Item name="publish" valuePropName="checked">
          <Checkbox>Publish now</Checkbox>
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            {isUpdate ? 'Update' : 'Submit'}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default AddNews;
